import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import AdminLayout from '../components/AdminLayout';
import '../styles/Admin.css';

interface InvoiceSettings {
  _id?: string;
  template: {
    html: string;
    css: string;
  };
  companyInfo: {
    name: string;
    nameEn: string;
    address: string;
    addressEn: string;
    phone: string;
    email: string;
    website?: string;
    taxNumber?: string;
    crNumber?: string;
    logo?: string;
  };
  autoSend: boolean;
  emailSettings: {
    enabled: boolean;
    subject: string;
    subjectEn: string;
    message: string;
    messageEn: string;
  };
  invoicePrefix: string;
  startingNumber: number;
  currentNumber: number;
}

interface Invoice {
  _id: string;
  invoiceNumber: string;
  bookingId: string;
  customerInfo: {
    name: string;
    phone: string;
    email: string;
    eventDate: string;
    eventLocation: string;
  };
  items: Array<{
    name: string;
    description: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
  }>;
  subtotal: number;
  discount?: number;
  discountCode?: string;
  tax?: number;
  taxRate?: number;
  total: number;
  status: 'draft' | 'sent' | 'paid' | 'cancelled';
  sentAt?: string;
  paidAt?: string;
  pdfUrl?: string;
  createdAt: string;
  updatedAt: string;
}


const InvoicesManagementFixed: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'invoices' | 'settings'>('invoices');
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [settings, setSettings] = useState<InvoiceSettings | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (activeTab === 'invoices') {
      loadInvoices();
    } else {
      loadSettings();
    }
  }, [activeTab, currentPage, statusFilter, searchQuery]);

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'https://fotomista-backend-production.up.railway.app';
      const response = await fetch(`${baseUrl}/api/invoices?page=${currentPage}&limit=10&status=${statusFilter}&search=${searchQuery}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('fotomista_token') || 'temp-admin-token-123'}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setInvoices(data.invoices || []);
        setTotalPages(data.pagination?.pages || 1);
      }
    } catch (error) {
      console.error('Error loading invoices:', error);
      setInvoices([]);
    }
    setLoading(false);
  };

  const loadSettings = async () => {
    setLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'https://fotomista-backend-production.up.railway.app';
      const response = await fetch(`${baseUrl}/api/invoices/settings`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('fotomista_token') || 'temp-admin-token-123'}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(data.settings);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      // Create default settings if loading fails
      setSettings({
        template: {
          html: '<!DOCTYPE html><html><body><h1>فاتورة</h1></body></html>',
          css: 'body { font-family: Arial, sans-serif; direction: rtl; }'
        },
        companyInfo: {
          name: 'فوتوميستا',
          nameEn: 'Fotomista',
          address: 'المملكة العربية السعودية',
          addressEn: 'Saudi Arabia',
          phone: '+966',
          email: 'fotomista.sa@gmail.com',
          website: 'www.fotomista.com'
        },
        autoSend: false,
        emailSettings: {
          enabled: true,
          subject: 'فاتورة خدمة فوتوميستا',
          subjectEn: 'Fotomista Invoice',
          message: 'مرفق فاتورة خدمة فوتوميستا',
          messageEn: 'Please find attached your Fotomista invoice'
        },
        invoicePrefix: 'INV',
        startingNumber: 1000,
        currentNumber: 1000
      });
    }
    setLoading(false);
  };

  const saveSettings = async () => {
    if (!settings) return;
    
    setSaving(true);
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'https://fotomista-backend-production.up.railway.app';
      const response = await fetch(`${baseUrl}/api/invoices/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer temp-admin-token-123'
        },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        alert('تم حفظ الإعدادات بنجاح');
      } else {
        alert('خطأ في حفظ الإعدادات');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('خطأ في حفظ الإعدادات');
    }
    setSaving(false);
  };

  const previewInvoice = async (invoiceId: string) => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'https://fotomista-backend-production.up.railway.app';
      // First fetch the HTML content
      const response = await fetch(`${baseUrl}/api/invoices/${invoiceId}/preview`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('fotomista_token') || 'temp-admin-token-123'}`
        }
      });
      
      if (response.ok) {
        const html = await response.text();
        // Open a new window and write the HTML content
        const previewWindow = window.open('', '_blank');
        if (previewWindow) {
          previewWindow.document.write(html);
          previewWindow.document.close();
        }
      } else {
        console.error('Failed to fetch preview:', response.status);
        alert('فشل في تحميل المعاينة');
      }
    } catch (error) {
      console.error('Error previewing invoice:', error);
      alert('خطأ في معاينة الفاتورة');
    }
  };

  const generatePDF = async (invoiceId: string) => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'https://fotomista-backend-production.up.railway.app';
      const response = await fetch(`${baseUrl}/api/invoices/${invoiceId}/pdf`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('fotomista_token') || 'temp-admin-token-123'}`
        }
      });
      
      if (response.ok) {
        alert('تم إنشاء ملف PDF بنجاح');
        loadInvoices();
      } else {
        alert('خطأ في إنشاء ملف PDF');
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('خطأ في إنشاء ملف PDF');
    }
  };

  const sendInvoice = async (invoiceId: string) => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'https://fotomista-backend-production.up.railway.app';
      const response = await fetch(`${baseUrl}/api/invoices/${invoiceId}/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('fotomista_token') || 'temp-admin-token-123'}`
        }
      });
      
      if (response.ok) {
        alert('تم إرسال الفاتورة بنجاح');
        loadInvoices();
      } else {
        alert('خطأ في إرسال الفاتورة');
      }
    } catch (error) {
      console.error('Error sending invoice:', error);
      alert('خطأ في إرسال الفاتورة');
    }
  };

  const getStatusBadge = (status: string) => {
    const labels: Record<string, string> = {
      draft: 'مسودة',
      sent: 'مرسلة',
      paid: 'مدفوعة',
      cancelled: 'ملغية'
    };

    return (
      <span className={`badge badge-${status}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading && !settings && !invoices.length) {
    return (
      <AdminLayout title="إدارة الفواتير">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>جاري التحميل...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="إدارة الفواتير">
      <motion.div 
        className="invoices-overview"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="section-header">
          <h1>إدارة الفواتير</h1>
          <p>إدارة الفواتير وإعداداتها</p>
        </div>

        <div className="tabs-container">
          <div className="tabs-nav">
            <button
              className={`tab-btn ${activeTab === 'invoices' ? 'active' : ''}`}
              onClick={() => setActiveTab('invoices')}
            >
              الفواتير
            </button>
            <button
              className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveTab('settings')}
            >
              إعدادات الفواتير
            </button>
          </div>
        </div>

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <div>
            <div className="card">
              <div className="filters-grid">
                <div>
                  <label className="form-label">
                    البحث
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      className="form-input pl-10"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="رقم الفاتورة أو اسم العميل"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <label className="form-label">
                    حالة الفاتورة
                  </label>
                  <select
                    className="form-input"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">جميع الحالات</option>
                    <option value="draft">مسودة</option>
                    <option value="sent">مرسلة</option>
                    <option value="paid">مدفوعة</option>
                    <option value="cancelled">ملغية</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="card">
              {loading ? (
                <div className="text-center">
                  <p>جاري تحميل الفواتير...</p>
                </div>
              ) : invoices.length === 0 ? (
                <div className="text-center">
                  <p>لا توجد فواتير</p>
                </div>
              ) : (
                <div>
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr className="table-header">
                          <th>
                            رقم الفاتورة
                          </th>
                          <th>
                            العميل
                          </th>
                          <th>
                            المبلغ
                          </th>
                          <th>
                            الحالة
                          </th>
                          <th>
                            التاريخ
                          </th>
                          <th>
                            الإجراءات
                          </th>
                        </tr>
                      </thead>
                      <tbody style={{ backgroundColor: 'white' }}>
                        {invoices.map((invoice, index) => (
                          <tr key={invoice._id} className="table-row">
                            <td>
                              {invoice.invoiceNumber}
                            </td>
                            <td>
                              <div>
                                <div className="customer-name">{invoice.customerInfo.name}</div>
                                <div className="customer-email">{invoice.customerInfo.email}</div>
                              </div>
                            </td>
                            <td>
                              {invoice.total} ريال
                            </td>
                            <td>
                              {getStatusBadge(invoice.status)}
                            </td>
                            <td>
                              {new Date(invoice.createdAt).toLocaleDateString('en-US')}
                            </td>
                            <td>
                              <div className="actions-buttons">
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => previewInvoice(invoice._id)}
                                >
                                  معاينة
                                </button>
                                <button
                                  className="btn btn-sm btn-secondary"
                                  onClick={() => sendInvoice(invoice._id)}
                                >
                                  إرسال
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination-container">
                <div className="pagination">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && settings && (
          <div>
            <div className="card">
              <h2 className="card-title">معلومات الشركة</h2>
              <div className="filters-grid">
                <div>
                  <label className="form-label">
                    اسم الشركة (عربي)
                  </label>
                  <input
                    type="text"
                    value={settings.companyInfo.name}
                    onChange={(e) => setSettings({
                      ...settings,
                      companyInfo: { ...settings.companyInfo, name: e.target.value }
                    })}
                    className="form-input"
                  />
                </div>
                <div>
                  <label className="form-label">
                    اسم الشركة (إنجليزي)
                  </label>
                  <input
                    type="text"
                    value={settings.companyInfo.nameEn}
                    onChange={(e) => setSettings({
                      ...settings,
                      companyInfo: { ...settings.companyInfo, nameEn: e.target.value }
                    })}
                    className="form-input"
                  />
                </div>
                <div>
                  <label className="form-label">
                    العنوان (عربي)
                  </label>
                  <input
                    type="text"
                    value={settings.companyInfo.address}
                    onChange={(e) => setSettings({
                      ...settings,
                      companyInfo: { ...settings.companyInfo, address: e.target.value }
                    })}
                    className="form-input"
                  />
                </div>
                <div>
                  <label className="form-label">
                    رقم الهاتف
                  </label>
                  <input
                    type="text"
                    value={settings.companyInfo.phone}
                    onChange={(e) => setSettings({
                      ...settings,
                      companyInfo: { ...settings.companyInfo, phone: e.target.value }
                    })}
                    className="form-input"
                  />
                </div>
                <div>
                  <label className="form-label">
                    البريد الإلكتروني
                  </label>
                  <input
                    type="email"
                    value={settings.companyInfo.email}
                    onChange={(e) => setSettings({
                      ...settings,
                      companyInfo: { ...settings.companyInfo, email: e.target.value }
                    })}
                    className="form-input"
                  />
                </div>
                <div>
                  <label className="form-label">
                    الموقع الإلكتروني
                  </label>
                  <input
                    type="text"
                    value={settings.companyInfo.website || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      companyInfo: { ...settings.companyInfo, website: e.target.value }
                    })}
                    className="form-input"
                  />
                </div>
              </div>
            </div>

            <div className="card">
              <h2 className="card-title">إعدادات الإيميل</h2>
              <div style={{ marginBottom: '1rem' }}>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={settings.emailSettings.enabled}
                    onChange={(e) => setSettings({
                      ...settings,
                      emailSettings: { ...settings.emailSettings, enabled: e.target.checked }
                    })}
                    className="checkbox-input"
                  />
                  <span>
                    تفعيل إرسال الفواتير بالإيميل
                  </span>
                </label>
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={settings.autoSend}
                    onChange={(e) => setSettings({
                      ...settings,
                      autoSend: e.target.checked
                    })}
                    className="checkbox-input"
                  />
                  <span>
                    إرسال الفاتورة تلقائياً بمجرد إنشاء الحجز
                  </span>
                </label>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                  عنوان الإيميل (عربي)
                </label>
                <input
                  type="text"
                  value={settings.emailSettings.subject}
                  onChange={(e) => setSettings({
                    ...settings,
                    emailSettings: { ...settings.emailSettings, subject: e.target.value }
                  })}
                  className="form-input"
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                  رسالة الإيميل (عربي)
                </label>
                <textarea
                  value={settings.emailSettings.message}
                  onChange={(e) => setSettings({
                    ...settings,
                    emailSettings: { ...settings.emailSettings, message: e.target.value }
                  })}
                  rows={3}
                  className="form-input form-textarea"
                />
              </div>
            </div>

            <div className="card">
              <div className="form-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3>قالب الفاتورة</h3>
                  <button
                    type="button"
                    onClick={() => setShowPreview(!showPreview)}
                    className="btn btn-secondary"
                  >
                    {showPreview ? 'إخفاء المعاينة' : 'عرض المعاينة'}
                  </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: showPreview ? '1fr 1fr' : '1fr', gap: '20px' }}>
                  <div>
                    <div className="variable-help">
                      <h4>المتغيرات المتاحة في قالب الفاتورة:</h4>
                      <div className="variables-grid">
                        <div className="variable-tag">{`{{companyInfo.name}}`}</div>
                        <div className="variable-tag">{`{{companyInfo.nameEn}}`}</div>
                        <div className="variable-tag">{`{{companyInfo.address}}`}</div>
                        <div className="variable-tag">{`{{companyInfo.addressEn}}`}</div>
                        <div className="variable-tag">{`{{companyInfo.phone}}`}</div>
                        <div className="variable-tag">{`{{companyInfo.email}}`}</div>
                        <div className="variable-tag">{`{{companyInfo.website}}`}</div>
                        <div className="variable-tag">{`{{companyInfo.taxNumber}}`}</div>
                        <div className="variable-tag">{`{{companyInfo.crNumber}}`}</div>
                        <div className="variable-tag">{`{{companyInfo.logo}}`}</div>
                        <div className="variable-tag">{`{{invoiceNumber}}`}</div>
                        <div className="variable-tag">{`{{invoiceDate}}`}</div>
                        <div className="variable-tag">{`{{dueDate}}`}</div>
                        <div className="variable-tag">{`{{customer.name}}`}</div>
                        <div className="variable-tag">{`{{customer.phone}}`}</div>
                        <div className="variable-tag">{`{{customer.email}}`}</div>
                        <div className="variable-tag">{`{{customer.eventDate}}`}</div>
                        <div className="variable-tag">{`{{customer.eventLocation}}`}</div>
                        <div className="variable-tag">{`{{subtotal}}`}</div>
                        <div className="variable-tag">{`{{discount}}`}</div>
                        <div className="variable-tag">{`{{discountCode}}`}</div>
                        <div className="variable-tag">{`{{tax}}`}</div>
                        <div className="variable-tag">{`{{taxRate}}`}</div>
                        <div className="variable-tag">{`{{total}}`}</div>
                      </div>
                      <p style={{ marginTop: '15px', fontSize: '13px', color: '#666' }}>
                        <strong>للعناصر (البنود):</strong> استخدم {`{{#each items}}`} ثم {`{{name}}`} و {`{{description}}`} و {`{{quantity}}`} (عدد الساعات) و {`{{unitPrice}}`} و {`{{totalPrice}}`} ثم {`{{/each}}`}
                      </p>
                    </div>

                    <div className="info-box">
                      <h3>دليل المتغيرات المتاحة</h3>
                      
                      <h4 style={{ color: '#0c5460', marginTop: '20px', marginBottom: '10px' }}>معلومات الشركة:</h4>
                      <ul style={{ margin: '0 0 15px 0', padding: '0 20px' }}>
                        <li><code>{`{{companyInfo.name}}`}</code> - اسم الشركة</li>
                        <li><code>{`{{companyInfo.nameEn}}`}</code> - اسم الشركة بالإنجليزية</li>
                        <li><code>{`{{companyInfo.address}}`}</code> - عنوان الشركة</li>
                        <li><code>{`{{companyInfo.addressEn}}`}</code> - عنوان الشركة بالإنجليزية</li>
                        <li><code>{`{{companyInfo.phone}}`}</code> - رقم هاتف الشركة</li>
                        <li><code>{`{{companyInfo.email}}`}</code> - إيميل الشركة</li>
                        <li><code>{`{{companyInfo.website}}`}</code> - موقع الشركة الإلكتروني</li>
                        <li><code>{`{{companyInfo.taxNumber}}`}</code> - الرقم الضريبي (اختياري)</li>
                        <li><code>{`{{companyInfo.crNumber}}`}</code> - رقم السجل التجاري (اختياري)</li>
                        <li><code>{`{{companyInfo.logo}}`}</code> - رابط شعار الشركة (اختياري)</li>
                      </ul>

                      <h4 style={{ color: '#0c5460', marginTop: '20px', marginBottom: '10px' }}>تفاصيل الفاتورة:</h4>
                      <ul style={{ margin: '0 0 15px 0', padding: '0 20px' }}>
                        <li><code>{`{{invoiceNumber}}`}</code> - رقم الفاتورة (يتم توليده تلقائياً)</li>
                        <li><code>{`{{invoiceDate}}`}</code> - تاريخ إنشاء الفاتورة</li>
                        <li><code>{`{{dueDate}}`}</code> - تاريخ الاستحقاق (30 يوم من الإنشاء)</li>
                      </ul>

                      <h4 style={{ color: '#0c5460', marginTop: '20px', marginBottom: '10px' }}>معلومات العميل:</h4>
                      <ul style={{ margin: '0 0 15px 0', padding: '0 20px' }}>
                        <li><code>{`{{customer.name}}`}</code> - اسم العميل</li>
                        <li><code>{`{{customer.phone}}`}</code> - رقم هاتف العميل</li>
                        <li><code>{`{{customer.email}}`}</code> - إيميل العميل</li>
                        <li><code>{`{{customer.eventDate}}`}</code> - تاريخ الفعالية</li>
                        <li><code>{`{{customer.eventLocation}}`}</code> - مكان الفعالية</li>
                      </ul>

                      <h4 style={{ color: '#0c5460', marginTop: '20px', marginBottom: '10px' }}>المجاميع المالية:</h4>
                      <ul style={{ margin: '0 0 15px 0', padding: '0 20px' }}>
                        <li><code>{`{{subtotal}}`}</code> - المجموع الفرعي (قبل الخصم والضريبة)</li>
                        <li><code>{`{{discount}}`}</code> - مبلغ الخصم (إن وجد)</li>
                        <li><code>{`{{discountCode}}`}</code> - كود الخصم المستخدم (إن وجد)</li>
                        <li><code>{`{{tax}}`}</code> - مبلغ الضريبة (حالياً = 0)</li>
                        <li><code>{`{{taxRate}}`}</code> - نسبة الضريبة (حالياً = 0%)</li>
                        <li><code>{`{{total}}`}</code> - المجموع النهائي</li>
                      </ul>

                      <h4 style={{ color: '#0c5460', marginTop: '20px', marginBottom: '10px' }}>بنود الفاتورة:</h4>
                      <p style={{ margin: '0 0 10px 0', padding: '0 20px', fontSize: '14px' }}>
                        لعرض بنود الفاتورة، استخدم:
                      </p>
                      <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '5px', margin: '0 20px 15px 20px' }}>
                        <code style={{ fontSize: '12px', lineHeight: '1.6' }}>
                          {`{{#each items}}`}<br/>
                          &nbsp;&nbsp;&lt;tr&gt;<br/>
                          &nbsp;&nbsp;&nbsp;&nbsp;&lt;td&gt;{`{{name}}`}&lt;/td&gt;<br/>
                          &nbsp;&nbsp;&nbsp;&nbsp;&lt;td&gt;{`{{description}}`}&lt;/td&gt;<br/>
                          &nbsp;&nbsp;&nbsp;&nbsp;&lt;td&gt;{`{{quantity}}`}&lt;/td&gt;<br/>
                          &nbsp;&nbsp;&nbsp;&nbsp;&lt;td&gt;{`{{unitPrice}}`}&lt;/td&gt;<br/>
                          &nbsp;&nbsp;&nbsp;&nbsp;&lt;td&gt;{`{{totalPrice}}`}&lt;/td&gt;<br/>
                          &nbsp;&nbsp;&lt;/tr&gt;<br/>
                          {`{{/each}}`}
                        </code>
                      </div>
                      <ul style={{ margin: '0 0 15px 0', padding: '0 40px', fontSize: '13px' }}>
                        <li><code>{`{{name}}`}</code> - اسم البند (مثل: "تصوير بالبوث - ساعتان")</li>
                        <li><code>{`{{description}}`}</code> - وصف البند</li>
                        <li><code>{`{{quantity}}`}</code> - عدد الساعات</li>
                        <li><code>{`{{unitPrice}}`}</code> - سعر الوحدة</li>
                        <li><code>{`{{totalPrice}}`}</code> - السعر الإجمالي لهذا البند</li>
                      </ul>
                    </div>
                    
                    <div className="field-group">
                      <label>قالب HTML + CSS المدمج</label>
                      <textarea
                        value={`<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <style>
${settings.template.css}
    </style>
</head>
<body>
${settings.template.html}
</body>
</html>`}
                        onChange={(e) => {
                          const fullTemplate = e.target.value;
                          
                          // استخراج CSS من بين <style> tags
                          const cssMatch = fullTemplate.match(/<style[^>]*>([\s\S]*?)<\/style>/i);
                          const extractedCSS = cssMatch ? cssMatch[1].trim() : '';
                          
                          // استخراج HTML من بين <body> tags
                          const htmlMatch = fullTemplate.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
                          const extractedHTML = htmlMatch ? htmlMatch[1].trim() : fullTemplate;
                          
                          setSettings({
                            ...settings,
                            template: { 
                              html: extractedHTML,
                              css: extractedCSS
                            }
                          });
                        }}
                        rows={20}
                        className="form-input form-textarea code-input"
                        style={{ fontFamily: 'monospace', fontSize: '13px', lineHeight: '1.4' }}
                        placeholder="<!DOCTYPE html>&#10;<html>&#10;<head>&#10;    <style>&#10;        /* CSS styles here */&#10;    </style>&#10;</head>&#10;<body>&#10;    <!-- HTML content here -->&#10;</body>&#10;</html>"
                      />
                    </div>
                  </div>

                  {showPreview && (
                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#2c3e50', marginBottom: '8px' }}>
                        معاينة مباشرة
                      </label>
                      <div style={{
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        height: '500px',
                        overflow: 'auto',
                        background: 'white'
                      }}>
                        <iframe
                          srcDoc={`<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <style>
${settings.template.css}
    </style>
</head>
<body>
${settings.template.html}
</body>
</html>`}
                          style={{
                            width: '100%',
                            height: '100%',
                            border: 'none',
                            borderRadius: '6px'
                          }}
                          title="معاينة الفاتورة"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button
                onClick={saveSettings}
                disabled={saving}
                className={`btn btn-primary ${saving ? 'disabled' : ''}`}
              >
                {saving ? 'جاري الحفظ...' : 'حفظ الإعدادات'}
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </AdminLayout>
  );
};

export default InvoicesManagementFixed;